import type { AccessoryProduct } from "@constants/formDefaults";
import LINKS from "@constants/links";
import AddIcon from "@mui/icons-material/Add";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, Button, CardContent, CardHeader, IconButton, Stack, Typography } from "@mui/material";
import { useGetDearProducts } from "@queries/useGetDearProducts";
import { calculateCartWeight } from "@utils/priceUtils";
import { formatCurrency, type SupportedCurrency } from "@utils/currencyUtils";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import LoadingOverlay from "../LoadingOverlay";

interface AccessoriesProps {
  hasAttemptedValidation?: boolean;
  goNext?: () => void;
  goBack?: () => void;
  isLoading?: boolean;
  title?: string;
}

export default function Accessories({ goNext, goBack, isLoading = false, title = "3. Please select any optional accessories" }: AccessoriesProps) {
  const { control, watch, setValue } = useFormContext();
  const accessories = watch("accessories") || {};
  const categories = watch("accessoryCategories");
  const products = watch("accessoryProducts");
  const selectedCurrency = (watch("selectedCurrency") as SupportedCurrency) || "usd";
  const [isLoadingAccessories, setIsLoadingAccessories] = useState(false);

  // Check if any accessories are selected
  const hasAccessoriesSelected = Object.values(accessories as Record<string, number>).some((qty) => qty > 0);

  const { refetch } = useGetDearProducts(categories, selectedCurrency);

  useEffect(() => {
    // Only fetch products if we have categories and we're on the accessories step
    if (categories.length > 0 && products.length === 0) {
      setIsLoadingAccessories(true);
      refetch()
        .then((res) => {
          if (res.data) {
            setValue("accessoryProducts", res.data?.products);
          }
        })
        .finally(() => {
          setIsLoadingAccessories(false);
        });
    }
  }, [categories, refetch, setValue, products]);

  const updateQuantity = (product: AccessoryProduct, delta: number, onChange: (value: Record<string, number>) => void) => {
    const current = accessories[product.SKU] || 0;
    const updatedQty = Math.max(0, current + delta);
    const newAccessories = { ...accessories };

    if (updatedQty === 0) {
      delete newAccessories[product.SKU];
    } else {
      newAccessories[product.SKU] = updatedQty;
    }

    onChange(newAccessories);

    const existingItems: { sku: string; quantity: number }[] = watch("items") || [];
    const filtered = existingItems.filter((item) => item.sku !== product.SKU);

    if (updatedQty > 0) {
      filtered.push({ sku: product.SKU, quantity: updatedQty });
    }

    setValue("items", filtered);
    // CartInfoContext automatically computes optimistic line items from accessories and items

    const totalWeight = calculateCartWeight({
      items: filtered,
      accessoryProducts: products,
    });

    setValue("accessoryTotalWeight", totalWeight);
  };

  return (
    <Stack>
      <CardHeader title={title} slotProps={{ title: { variant: "h5" } }} />
      <CardContent>
        <Typography gutterBottom>Optionally add any of these accessories to your order. Shipping costs (if any) will be calculated at the next step.</Typography>
      </CardContent>

      {(goNext || goBack) && (
        <Stack m={2} justifyContent="space-between" direction="row">
          {goBack && (
            <Button onClick={goBack} loading={isLoading}>
              Back
            </Button>
          )}
          {goNext && (
            <Button variant="contained" onClick={goNext} loading={isLoading}>
              {hasAccessoriesSelected ? "Continue" : "No Thanks"}
            </Button>
          )}
        </Stack>
      )}

      {categories?.includes("ssp") && (
        <Box>
          <CardHeader title="SSP Headphones" slotProps={{ title: { variant: "h5" } }} />
          <CardContent>
            <Typography>
              We recommend over-the-ear (circumaural) headphones for their enclosed nature and immersive sound experience with the SSP. You can use your own headphones or purchase Unyte Health headphones. See{" "}
              <a target="_blank" rel="noreferrer" href={LINKS.support.sspHeadphonesUrl}>
                here
              </a>{" "}
              for other headphone options.
            </Typography>
          </CardContent>
        </Box>
      )}
      <Controller
        name="accessories"
        control={control}
        render={({ field }) => (
          <Stack>
            {products.map((product: AccessoryProduct) => {
              const quantity = accessories[product.SKU] || 0;
              const thumbnail = product.Attachments?.[0];

              return (
                <Box p={2} key={product.SKU}>
                  <Box display="flex" gap={2} alignItems="flex-start">
                    <Box
                      sx={{
                        width: "200px",
                        height: "200px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "8px",
                        flexShrink: 0,
                      }}
                    >
                      {thumbnail ? (
                        <Box
                          component="img"
                          src={thumbnail}
                          alt={product.Name}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "8px",
                          }}
                        />
                      ) : (
                        <HeadphonesIcon sx={{ fontSize: "40px", color: "gray" }} />
                      )}
                    </Box>
                    <Box flex="1">
                      <Typography fontWeight={500}>{product.Name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {product.Description}
                      </Typography>
                      <Stack>
                        <Typography variant="body1" mt={1} sx={{ fontSize: "1.5rem", px: 1 }}>
                          {formatCurrency(product.PriceTier1, selectedCurrency)}
                        </Typography>

                        <Box display="flex" alignItems="center" mt={2}>
                          <IconButton size="small" onClick={() => updateQuantity(product, -1, field.onChange)} disabled={quantity === 0}>
                            <RemoveIcon />
                          </IconButton>
                          <Typography width="24px" textAlign="center">
                            {quantity}
                          </Typography>
                          <IconButton size="small" onClick={() => updateQuantity(product, 1, field.onChange)}>
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      />

      <LoadingOverlay open={isLoadingAccessories} message="fetching accessories..." />
    </Stack>
  );
}
